import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { Settings, User, GraduationCap, Award, Bell, Plus, X, AlertCircle, Edit2, Trash2, CheckCircle, Circle, FileText, Calendar, TestTube, ClipboardCheck, Eye, EyeOff, Lock, CreditCard } from 'lucide-react';
import { stateRequirements } from '../stateRequirements';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';

const SettingsPage = () => {
  const [user, setUser] = useState(null);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignmentTypes, setAssignmentTypes] = useState([
    { id: '1', name: 'Test', weight: 40 },
    { id: '2', name: 'Quiz', weight: 20 },
    { id: '3', name: 'Homework', weight: 20 },
    { id: '4', name: 'Classwork', weight: 20 }
  ]);
  const [semesters, setSemesters] = useState([]);
  const [newSemester, setNewSemester] = useState({ name: '', startDate: '', endDate: '' });
  const [isAddChildDialogOpen, setIsAddChildDialogOpen] = useState(false);
  const [isEditPinDialogOpen, setIsEditPinDialogOpen] = useState(false);
  const [selectedChildLogin, setSelectedChildLogin] = useState(null);
  const [editingChild, setEditingChild] = useState(null);
  const [childFormData, setChildFormData] = useState({
    name: '',
    grade: '',
    age: '',
    username: '',
    pin: '',
    theme: 'purple'
  });
  const [selectedState, setSelectedState] = useState('California');
  const [requirements, setRequirements] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [isAddSubjectDialogOpen, setIsAddSubjectDialogOpen] = useState(false);
  const [isEditSubjectDialogOpen, setIsEditSubjectDialogOpen] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [subjectFormData, setSubjectFormData] = useState({
    name: '',
    color: '#3B82F6',
    icon: '📚'
  });
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showCancelSubscription, setShowCancelSubscription] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [isEditTokensDialogOpen, setIsEditTokensDialogOpen] = useState(false);
  const [editingChildTokens, setEditingChildTokens] = useState(null);
  const [newTokenAmount, setNewTokenAmount] = useState(0);
  const { toast} = useToast();

  const loadSubjects = async () => {
    try {
      const data = await api.getSubjects();
      console.log('Loaded subjects:', data);
      // Sort subjects alphabetically by name
      const sortedSubjects = data.sort((a, b) => 
        a.name.localeCompare(b.name)
      );
      setSubjects(sortedSubjects);
    } catch (error) {
      console.error('Failed to load subjects:', error);
      toast({
        title: 'Error',
        description: 'Failed to load subjects',
        variant: 'destructive'
      });
    }
  };


  useEffect(() => {
    const loadAllData = async () => {
      try {
        await Promise.all([
          loadUser(),
          loadChildren(),
          loadSubjects()
        ]);
      } catch (error) {
        console.error('Failed to load settings data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load settings. Please refresh the page.',
          variant: 'destructive'
        });
      } finally {
        // Always set loading to false, even if some requests fail
        setLoading(false);
      }
    };
    loadAllData();
  }, []);

  useEffect(() => {
    const stateReqs = stateRequirements[selectedState]?.requirements || [];
    setRequirements(stateReqs);
  }, [selectedState]);


  const loadUser = async () => {
    try {
      const userData = await api.getCurrentUser();
      // Add default grading and reward properties if they don't exist
      setUser({
        ...userData,
        grading_enabled: userData.grading_enabled || false,
        show_grades_to_children: userData.show_grades_to_children || false,
        grading_scale: userData.grading_scale || {
          'A+': '98-100', 'A': '94-97', 'A-': '93',
          'B+': '90-92', 'B': '87-89', 'B-': '85-86',
          'C+': '82-84', 'C': '79-81', 'C-': '77-78',
          'D+': '74-76', 'D': '71-73', 'D-': '70',
          'F': '0-69'
        },
        reward_store_enabled: userData.reward_store_enabled || false,
        token_conversion_rate: userData.token_conversion_rate || 10
      });
      
      // Load assignment types and semesters from backend
      if (userData.assignment_types && userData.assignment_types.length > 0) {
        setAssignmentTypes(userData.assignment_types);
      }
      if (userData.semesters && userData.semesters.length > 0) {
        // Normalize semester data - handle both camelCase (old) and snake_case (new)
        const normalizedSemesters = userData.semesters.map(sem => ({
          name: sem.name,
          start_date: sem.start_date || sem.startDate,
          end_date: sem.end_date || sem.endDate
        }));
        setSemesters(normalizedSemesters);
      } else {
        // Set default semesters if none exist (using snake_case)
        const defaultSemesters = [
          { name: 'Fall 2024', start_date: '2024-08-01', end_date: '2024-12-31' },
          { name: 'Spring 2025', start_date: '2025-01-01', end_date: '2025-05-31' },
          { name: 'Summer 2025', start_date: '2025-06-01', end_date: '2025-07-31' },
          { name: 'Fall 2025', start_date: '2025-08-01', end_date: '2025-12-31' }
        ];
        setSemesters(defaultSemesters);
        // Auto-save defaults to backend
        try {
          await api.updateProfile({ semesters: defaultSemesters });
        } catch (err) {
          console.error('Failed to save default semesters:', err);
        }
      }
    } catch (error) {
      console.error('Failed to load user:', error);
      toast({
        title: 'Error',
        description: 'Failed to load profile',
        variant: 'destructive'
      });
    }
  };

  const loadChildren = async () => {
    try {
      const data = await api.getChildren();
      setChildren(data);
    } catch (error) {
      console.error('Failed to load children:', error);
      toast({
        title: 'Error',
        description: 'Failed to load children',
        variant: 'destructive'
      });
    }
  };

  const handleSaveProfile = async () => {
    try {
      console.log('Saving assignment types:', assignmentTypes);
      console.log('Saving semesters:', semesters);
      await api.updateProfile({
        name: user.name,
        email: user.email,
        phone: user.phone,
        state: user.state,
        school_name: user.school_name,
        timezone: user.timezone,
        grading_enabled: user.grading_enabled,
        show_grades_to_children: user.show_grades_to_children,
        grading_scale: user.grading_scale,
        reward_store_enabled: user.reward_store_enabled,
        token_conversion_rate: user.token_conversion_rate,
        assignment_types: assignmentTypes,
        semesters: semesters
      });
      
      // DON'T reload - keep the user's current state
      
      toast({
        title: 'Settings Saved!',
        description: 'Your settings have been updated successfully.'
      });
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update settings',
        variant: 'destructive'
      });
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: 'Password Mismatch',
        description: 'New passwords do not match',
        variant: 'destructive'
      });
      return;
    }

    if (passwordData.newPassword.length < 8) {
      toast({
        title: 'Weak Password',
        description: 'Password must be at least 8 characters long',
        variant: 'destructive'
      });
      return;
    }

    setPasswordLoading(true);

    try {
      await api.request('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        })
      });

      toast({
        title: 'Password Changed!',
        description: 'Your password has been updated successfully.'
      });

      setShowChangePassword(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to change password',
        variant: 'destructive'
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);

    try {
      await api.request('/api/subscription/cancel', {
        method: 'POST'
      });

      toast({
        title: 'Subscription Cancelled',
        description: 'Your subscription has been cancelled. You will retain access until the end of your billing period.'
      });

      setShowCancelSubscription(false);
      
      // Reload user data to reflect cancellation
      const userData = await api.getProfile();
      setUser(userData);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to cancel subscription',
        variant: 'destructive'
      });
    } finally {
      setCancelLoading(false);
    }
  };

  const handleGradeScaleChange = (grade, field, value) => {
    setUser({
      ...user,
      grading_scale: {
        ...user.grading_scale,
        [grade]: {
          ...user.grading_scale[grade],
          [field]: parseInt(value)
        }
      }
    });
  };

  const addAssignmentType = () => {
    setAssignmentTypes([
      ...assignmentTypes,
      { id: String(Date.now()), name: '', weight: 0 }
    ]);
  };

  const removeAssignmentType = (id) => {
    setAssignmentTypes(assignmentTypes.filter(t => t.id !== id));
  };

  const updateAssignmentType = (id, field, value) => {
    setAssignmentTypes(assignmentTypes.map(t =>
      t.id === id ? { ...t, [field]: field === 'weight' ? parseFloat(value) || 0 : value } : t
    ));
  };

  const totalWeight = assignmentTypes.reduce((sum, t) => sum + (t.weight || 0), 0);
  const isWeightValid = Math.abs(totalWeight - 100) < 0.01;

  const addSemester = async () => {
    if (newSemester.name.trim() && newSemester.startDate && newSemester.endDate) {
      const newSemesterData = { 
        name: newSemester.name.trim(), 
        start_date: newSemester.startDate,  // Use snake_case for backend
        end_date: newSemester.endDate       // Use snake_case for backend
      };
      const updatedSemesters = [...semesters, newSemesterData];
      setSemesters(updatedSemesters);
      setNewSemester({ name: '', startDate: '', endDate: '' });
      
      // Auto-save to backend immediately
      try {
        await api.updateProfile({ semesters: updatedSemesters });
        toast({
          title: 'Semester Added!',
          description: 'Semester has been saved successfully.'
        });
      } catch (error) {
        console.error('Failed to save semester:', error);
        toast({
          title: 'Error',
          description: 'Failed to save semester. Please try again.',
          variant: 'destructive'
        });
      }
    }
  };

  const removeSemester = async (index) => {
    const updatedSemesters = semesters.filter((_, i) => i !== index);
    setSemesters(updatedSemesters);
    
    // Auto-save to backend immediately
    try {
      await api.updateProfile({ semesters: updatedSemesters });
      toast({
        title: 'Semester Removed',
        description: 'Semester has been deleted successfully.'
      });
    } catch (error) {
      console.error('Failed to remove semester:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove semester. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const handleAddChild = async () => {
    // Validate form
    if (!childFormData.name || !childFormData.grade) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in name and grade.',
        variant: 'destructive'
      });
      return;
    }

    // For new children, validate username and PIN
    if (!editingChild) {
      if (!childFormData.username || !childFormData.pin) {
        toast({
          title: 'Missing Information',
          description: 'Please fill in username and PIN.',
          variant: 'destructive'
        });
        return;
      }

      // Validate PIN is 4 digits
      if (!/^\d{4}$/.test(childFormData.pin)) {
        toast({
          title: 'Invalid PIN',
          description: 'PIN must be exactly 4 digits.',
          variant: 'destructive'
        });
        return;
      }
    }

    try {
      if (editingChild) {
        // Update existing child
        await api.updateChild(editingChild.id, {
          name: childFormData.name,
          grade: childFormData.grade,
          age: childFormData.age ? parseInt(childFormData.age) : null,
          theme: childFormData.theme
        });
        
        toast({
          title: 'Child Updated!',
          description: `${childFormData.name}'s profile has been updated.`
        });
      } else {
        // Create new child
        await api.createChild({
          name: childFormData.name,
          grade: childFormData.grade,
          age: childFormData.age ? parseInt(childFormData.age) : null,
          username: childFormData.username,
          pin: childFormData.pin,
          theme: childFormData.theme
        });
        
        toast({
          title: 'Child Added!',
          description: `${childFormData.name} has been added successfully.`
        });
      }

      // Reload children
      await loadChildren();

      // Reset form and close dialog
      setChildFormData({
        name: '',
        grade: '',
        age: '',
        username: '',
        pin: '',
        theme: 'purple'
      });
      setEditingChild(null);
      setIsAddChildDialogOpen(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add child',
        variant: 'destructive'
      });
    }
  };

  const handleRemoveChild = async (childId) => {
    if (window.confirm(`Are you sure you want to remove this child? This will delete all their data.`)) {
      try {
        await api.deleteChild(childId);
        
        toast({
          title: 'Child Removed',
          description: 'The child has been removed from the system.'
        });

        // Reload children
        await loadChildren();
      } catch (error) {
        toast({
          title: 'Error',
          description: error.message || 'Failed to remove child',
          variant: 'destructive'
        });
      }
    }
  };

  const handleEditPin = (child) => {
    setSelectedChildLogin(child);
    setChildFormData({ ...childFormData, pin: child.login.pin });
    setIsEditPinDialogOpen(true);
  };

  const handleSavePin = async () => {
    // Validate PIN is 4 digits
    if (!/^\d{4}$/.test(childFormData.pin)) {
      toast({
        title: 'Invalid PIN',
        description: 'PIN must be exactly 4 digits.',
        variant: 'destructive'
      });
      return;
    }

    try {
      await api.updateChildPin(selectedChildLogin.id, childFormData.pin);

      toast({
        title: 'PIN Updated!',
        description: 'The child login PIN has been changed successfully.'
      });

      // Reload children
      await loadChildren();

      setIsEditPinDialogOpen(false);
      setSelectedChildLogin(null);
      setChildFormData({ ...childFormData, pin: '' });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update PIN',
        variant: 'destructive'
      });
    }
  };

  const handleUpdateTokens = async () => {
    // Validate tokens is a non-negative number
    if (newTokenAmount < 0 || !Number.isInteger(newTokenAmount)) {
      toast({
        title: 'Invalid Amount',
        description: 'Token amount must be a non-negative whole number.',
        variant: 'destructive'
      });
      return;
    }

    try {
      await api.updateChildTokens(editingChildTokens.id, newTokenAmount);

      toast({
        title: 'Tokens Updated!',
        description: `${editingChildTokens.name}'s token balance has been updated to ${newTokenAmount}.`
      });

      // Reload children to get updated token counts
      await loadChildren();

      setIsEditTokensDialogOpen(false);
      setEditingChildTokens(null);
      setNewTokenAmount(0);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update tokens',
        variant: 'destructive'
      });
    }
  };


  // Subject Management Functions
  const handleAddSubject = async () => {
    try {
      await api.createSubject(subjectFormData);
      
      toast({
        title: 'Subject Added!',
        description: `${subjectFormData.name} has been created.`
      });

      setIsAddSubjectDialogOpen(false);
      setSubjectFormData({ name: '', color: '#3B82F6', icon: '📚' });
      await loadSubjects();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add subject',
        variant: 'destructive'
      });
    }
  };

  const handleEditSubject = (subject) => {
    setSelectedSubject(subject);
    setSubjectFormData({
      name: subject.name,
      color: subject.color,
      icon: subject.icon
    });
    // Use setTimeout to ensure state updates complete before opening dialog
    setTimeout(() => {
      setIsEditSubjectDialogOpen(true);
    }, 0);
  };

  const handleUpdateSubject = async () => {
    try {
      await api.updateSubject(selectedSubject.id, subjectFormData);
      
      toast({
        title: 'Subject Updated!',
        description: `${subjectFormData.name} has been updated.`
      });

      setIsEditSubjectDialogOpen(false);
      setSelectedSubject(null);
      setSubjectFormData({ name: '', color: '#3B82F6', icon: '📚' });
      await loadSubjects();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update subject',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteSubject = async (subjectId) => {
    if (window.confirm('Are you sure you want to delete this subject? This cannot be undone if it has no assignments.')) {
      try {
        await api.deleteSubject(subjectId);
        
        toast({
          title: 'Subject Deleted',
          description: 'The subject has been removed.'
        });

        await loadSubjects();
      } catch (error) {
        toast({
          title: 'Error',
          description: error.message || 'Cannot delete subject with existing assignments',
          variant: 'destructive'
        });
      }
    }
  };

  const handleToggleRequirement = (reqId) => {
    const updated = requirements.map(req =>
      req.id === reqId ? { ...req, completed: !req.completed } : req
    );
    setRequirements(updated);
  };

  const completedCount = requirements.filter(r => r.completed).length;
  const requirementsProgress = requirements.length > 0 ? (completedCount / requirements.length) * 100 : 0;

  const categoryIcons = {
    attendance: Calendar,
    testing: TestTube,
    notification: Bell,
    portfolio: FileText,
    reporting: ClipboardCheck,
    records: FileText,
    health: CheckCircle,
    qualification: CheckCircle,
    instruction: CheckCircle,
    regulation: CheckCircle
  };

  const categoryColors = {
    attendance: 'bg-blue-100 text-blue-700',
    testing: 'bg-purple-100 text-purple-700',
    notification: 'bg-amber-100 text-amber-700',
    portfolio: 'bg-green-100 text-green-700',
    reporting: 'bg-pink-100 text-pink-700',
    records: 'bg-teal-100 text-teal-700',
    health: 'bg-red-100 text-red-700',
    qualification: 'bg-indigo-100 text-indigo-700',
    instruction: 'bg-cyan-100 text-cyan-700',
    regulation: 'bg-slate-100 text-slate-700'
  };

  const groupedRequirements = requirements.reduce((acc, req) => {
    if (!acc[req.category]) acc[req.category] = [];
    acc[req.category].push(req);
    return acc;
  }, {});


  const states = Object.keys(stateRequirements).sort();

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-xl text-slate-600">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Settings</h1>
          <p className="text-slate-600 text-lg">Manage your account and preferences</p>
        </div>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            <TabsTrigger value="profile" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <User className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline">Profile</span>
              <span className="xs:hidden">👤</span>
            </TabsTrigger>
            <TabsTrigger value="grading" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <GraduationCap className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline">Grading</span>
              <span className="xs:hidden">🎓</span>
            </TabsTrigger>
            <TabsTrigger value="rewards" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <Award className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline">Rewards</span>
              <span className="xs:hidden">🏆</span>
            </TabsTrigger>
            <TabsTrigger value="subjects" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline">Subjects</span>
              <span className="xs:hidden">📚</span>
            </TabsTrigger>
            <TabsTrigger value="children" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <Settings className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline">Students</span>
              <span className="xs:hidden">👦</span>
            </TabsTrigger>
            <TabsTrigger value="requirements" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline">Requirements</span>
              <span className="xs:hidden">📋</span>
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card className="p-8 border-0 shadow-lg bg-white">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Profile Information</h2>
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={user.name}
                      onChange={(e) => setUser({ ...user, name: e.target.value })}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={user.email}
                      onChange={(e) => setUser({ ...user, email: e.target.value })}
                      className="mt-2"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      value={user.phone || ''}
                      onChange={(e) => setUser({ ...user, phone: e.target.value })}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="school_name">School Name</Label>
                    <Input
                      id="school_name"
                      value={user.school_name || ''}
                      onChange={(e) => setUser({ ...user, school_name: e.target.value })}
                      className="mt-2"
                      placeholder="e.g., Smith Family Homeschool"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="state">Homeschool State</Label>
                    <Select value={user.state} onValueChange={(value) => setUser({ ...user, state: value })}>
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {states.map(state => (
                          <SelectItem key={state} value={state}>
                            {stateRequirements[state].name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">
                      This determines your state requirements checklist
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="timezone">Time Zone</Label>
                    <Select value={user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone} onValueChange={(value) => setUser({ ...user, timezone: value })}>
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        <SelectItem value="America/New_York">Eastern Time (ET)</SelectItem>
                        <SelectItem value="America/Chicago">Central Time (CT)</SelectItem>
                        <SelectItem value="America/Denver">Mountain Time (MT)</SelectItem>
                        <SelectItem value="America/Phoenix">Arizona Time (MST)</SelectItem>
                        <SelectItem value="America/Los_Angeles">Pacific Time (PT)</SelectItem>
                        <SelectItem value="America/Anchorage">Alaska Time (AKT)</SelectItem>
                        <SelectItem value="Pacific/Honolulu">Hawaii Time (HST)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">
                      Used for date calculations (due dates, submissions)
                    </p>
                  </div>
                </div>

                <Button
                  onClick={handleSaveProfile}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Save Profile
                </Button>
              </div>
            </Card>

            {/* Change Password Section */}
            <Card className="p-8 border-0 shadow-lg bg-white mt-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Password & Security</h2>
                  <p className="text-slate-600 mt-1">Manage your password and account security</p>
                </div>
              </div>
              <Button
                onClick={() => setShowChangePassword(true)}
                variant="outline"
                className="w-full md:w-auto"
              >
                <Lock className="w-4 h-4 mr-2" />
                Change Password
              </Button>
            </Card>

            {/* Subscription Management Section */}
            <Card className="p-8 border-0 shadow-lg bg-white mt-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Subscription</h2>
                  <p className="text-slate-600 mt-1">Manage your subscription and billing</p>
                </div>
              </div>
              <div className="space-y-4">
                {user?.subscription_status === 'active' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-semibold text-green-900">Active Subscription</span>
                    </div>
                    <p className="text-sm text-green-700">
                      Your subscription is currently active. You have full access to all features.
                    </p>
                  </div>
                )}
                {user?.subscription_status === 'trial' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-5 h-5 text-blue-600" />
                      <span className="font-semibold text-blue-900">Free Trial</span>
                    </div>
                    <p className="text-sm text-blue-700">
                      You're currently on a free trial. Enjoy full access to all features!
                    </p>
                  </div>
                )}
                <Button
                  onClick={() => setShowCancelSubscription(true)}
                  variant="outline"
                  className="w-full md:w-auto border-red-300 text-red-600 hover:bg-red-50"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Cancel Subscription
                </Button>
              </div>
            </Card>
          </TabsContent>

          {/* Grading Tab */}
          <TabsContent value="grading">
            <Card className="p-8 border-0 shadow-lg bg-white">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Grading Settings</h2>
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="text-base">Enable Grading System</Label>
                    <p className="text-sm text-slate-600">Grade assignments and track academic progress</p>
                  </div>
                  <Switch
                    checked={user.grading_enabled}
                    onCheckedChange={(checked) => setUser({ ...user, grading_enabled: checked })}
                  />
                </div>

                {user.grading_enabled && (
                  <>
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <Label className="text-base">Show Grades to Children</Label>
                        <p className="text-sm text-slate-600">Children can see their letter grades</p>
                      </div>
                      <Switch
                        checked={user.show_grades_to_children}
                        onCheckedChange={(checked) => setUser({ ...user, show_grades_to_children: checked })}
                      />
                    </div>

                    {/* Assignment Types Configuration */}
                    <div className="border-t pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <Label className="text-lg font-semibold">Assignment Types & Weights</Label>
                          <p className="text-sm text-slate-600 mt-1">Define how different assignment types contribute to semester grades</p>
                        </div>
                        <Button
                          onClick={addAssignmentType}
                          size="sm"
                          className="bg-teal-600 hover:bg-teal-700 text-white"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add Type
                        </Button>
                      </div>

                      <div className="space-y-3">
                        {assignmentTypes.map((type, index) => (
                          <div key={type.id} className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                            <span className="text-sm font-bold text-slate-500 w-6">{index + 1}</span>
                            <div className="flex-1 grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs text-slate-600">Type Name</Label>
                                <Input
                                  value={type.name}
                                  onChange={(e) => updateAssignmentType(type.id, 'name', e.target.value)}
                                  placeholder="e.g., Test, Quiz, Homework"
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-slate-600">Weight (%)</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.1"
                                  value={type.weight}
                                  onChange={(e) => updateAssignmentType(type.id, 'weight', e.target.value)}
                                  placeholder="0-100"
                                  className="mt-1"
                                />
                              </div>
                            </div>
                            <Button
                              onClick={() => removeAssignmentType(type.id)}
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>

                      <div className={`mt-4 p-4 rounded-lg border-2 ${
                        isWeightValid 
                          ? 'bg-green-50 border-green-300' 
                          : 'bg-amber-50 border-amber-300'
                      }`}>
                        <div className="flex items-center gap-2">
                          {isWeightValid ? (
                            <>
                              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-xs">✓</span>
                              </div>
                              <div>
                                <p className="font-semibold text-green-900">Total Weight: {totalWeight.toFixed(1)}%</p>
                                <p className="text-sm text-green-700">Weights are correctly configured!</p>
                              </div>
                            </>
                          ) : (
                            <>
                              <AlertCircle className="w-6 h-6 text-amber-600" />
                              <div>
                                <p className="font-semibold text-amber-900">Total Weight: {totalWeight.toFixed(1)}%</p>
                                <p className="text-sm text-amber-700">Total weight must equal 100%</p>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Semester Configuration */}
                    <div className="border-t pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <Label className="text-lg font-semibold">Semesters / Terms</Label>
                          <p className="text-sm text-slate-600 mt-1">Define semester names and date ranges for grade reporting</p>
                        </div>
                      </div>

                      <div className="space-y-3 mb-4">
                        {semesters.map((semester, index) => (
                          <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                            <span className="text-sm font-bold text-slate-500 w-6">{index + 1}</span>
                            <div className="flex-1">
                              <div className="text-slate-900 font-medium">{semester.name}</div>
                              <div className="text-xs text-slate-500 mt-1">
                                {semester.start_date || semester.startDate} to {semester.end_date || semester.endDate}
                              </div>
                            </div>
                            <Button
                              onClick={() => removeSemester(index)}
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>

                      <div className="space-y-3">
                        <Input
                          value={newSemester.name}
                          onChange={(e) => setNewSemester({...newSemester, name: e.target.value})}
                          placeholder="Semester name (e.g., Fall 2025)"
                        />
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-slate-600">Start Date</Label>
                            <Input
                              type="date"
                              value={newSemester.startDate}
                              onChange={(e) => setNewSemester({...newSemester, startDate: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-600">End Date</Label>
                            <Input
                              type="date"
                              value={newSemester.endDate}
                              onChange={(e) => setNewSemester({...newSemester, endDate: e.target.value})}
                            />
                          </div>
                        </div>
                        <Button
                          onClick={addSemester}
                          className="w-full bg-teal-600 hover:bg-teal-700 text-white"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add
                        </Button>
                      </div>
                    </div>

                    <div>
                      <Label className="text-lg font-semibold mb-4 block">Grade Scale</Label>
                      <p className="text-sm text-slate-600 mb-4">Customize percentage ranges for each letter grade</p>
                      <div className="space-y-4">
                        {Object.entries(user.grading_scale).map(([grade, range]) => (
                          <div key={grade} className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-teal-500 rounded-lg flex items-center justify-center">
                              <span className="text-white font-bold text-xl">{grade}</span>
                            </div>
                            <div className="flex-1 grid grid-cols-2 gap-4">
                              <div>
                                <Label className="text-xs">Minimum %</Label>
                                <Input
                                  type="number"
                                  value={range.min}
                                  onChange={(e) => handleGradeScaleChange(grade, 'min', e.target.value)}
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Maximum %</Label>
                                <Input
                                  type="number"
                                  value={range.max}
                                  onChange={(e) => handleGradeScaleChange(grade, 'max', e.target.value)}
                                  className="mt-1"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                <Button
                  onClick={handleSaveProfile}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={!isWeightValid}
                >
                  Save Grading Settings
                </Button>
                {!isWeightValid && (
                  <p className="text-sm text-amber-600 mt-2">
                    ⚠️ Cannot save: Total weight must equal 100%
                  </p>
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Rewards Tab */}
          <TabsContent value="rewards">
            <Card className="p-8 border-0 shadow-lg bg-white">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Reward Settings</h2>
              <div className="space-y-6">
                <div>
                  <Label htmlFor="tokens">Tokens Per Assignment</Label>
                  <Input
                    id="tokens"
                    type="number"
                    value={user.tokens_per_assignment}
                    onChange={(e) => setUser({ ...user, tokens_per_assignment: parseInt(e.target.value) })}
                    className="mt-2 max-w-xs"
                  />
                  <p className="text-sm text-slate-600 mt-2">
                    Default token reward for completed assignments (you can customize per assignment)
                  </p>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Manage Student Tokens</h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Manually adjust token balances for bonus rewards or corrections
                  </p>
                  <div className="space-y-3">
                    {children.map(child => (
                      <div key={child.id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                            style={{ backgroundColor: child.profile_color || '#3B82F6' }}
                          >
                            {child.name.charAt(0)}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{child.name}</p>
                            <p className="text-sm text-slate-600">Current: {child.login?.tokens || 0} tokens</p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingChildTokens(child);
                            setNewTokenAmount(child.login?.tokens || 0);
                            setIsEditTokensDialogOpen(true);
                          }}
                          className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                        >
                          <Award className="w-4 h-4 mr-2" />
                          Adjust Tokens
                        </Button>
                      </div>
                    ))}
                    {children.length === 0 && (
                      <p className="text-sm text-slate-500 text-center py-4">No children added yet</p>
                    )}
                  </div>
                </div>

                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Award className="w-6 h-6 text-amber-600 mt-1" />
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">How Rewards Work</h3>
                      <ul className="text-sm text-slate-700 space-y-1">
                        <li>• Children earn tokens for completing assignments</li>
                        <li>• Tokens can be redeemed for digital stickers, pets, and badges</li>
                        <li>• You control the token value of each assignment</li>
                        <li>• Rewards are displayed in the child's dashboard</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleSaveProfile}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Save Reward Settings
                </Button>
              </div>
            </Card>
          </TabsContent>

          {/* Child Logins Tab */}


          {/* Subjects Tab */}
          <TabsContent value="subjects">
            <Card className="p-8 border-0 shadow-lg bg-white">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Manage Subjects</h2>
                  <p className="text-slate-600 mt-1">Customize your curriculum subjects</p>
                </div>
                <Button
                  onClick={() => setIsAddSubjectDialogOpen(true)}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Subject
                </Button>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {subjects.map(subject => {
                  const isDefaultSubject = ['1', '2', '3', '4', '5', '6', '7', '8'].includes(subject.id);
                  return (
                  <Card key={subject.id} className="p-4 border-2 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <div
                          className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl"
                          style={{ backgroundColor: `${subject.color}20`, color: subject.color }}
                        >
                          {subject.icon}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-slate-900">{subject.name}</h3>
                            {isDefaultSubject && (
                              <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">
                                Default
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500">{subject.color}</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditSubject(subject);
                          }}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        {/* Only show delete button for non-default subjects (IDs 1-8 are defaults) */}
                        {!isDefaultSubject && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSubject(subject.id);
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                  );
                })}
              </div>

              {subjects.length === 0 && (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No subjects yet. Add your first subject to get started!</p>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="children">
            <Card className="p-8 border-0 shadow-lg bg-white">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Student Management</h2>
                  <p className="text-slate-600 mt-1">Manage student information and login credentials</p>
                </div>
                <Button
                  onClick={() => setIsAddChildDialogOpen(true)}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Child
                </Button>
              </div>

              <div className="space-y-4">
                {loading ? (
                  <div className="text-center py-8">
                    <p className="text-slate-500">Loading children...</p>
                  </div>
                ) : children.map(child => {
                  return (
                    <div key={child.id} className="p-4 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors">
                      {/* Mobile and Desktop Layout */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        {/* Student Info - Left Side */}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                              style={{ backgroundColor: child.profile_color || '#3B82F6' }}
                            >
                              {child.name.charAt(0)}
                            </div>
                            <div>
                              <h3 className="font-semibold text-slate-900">{child.name}</h3>
                              <p className="text-sm text-slate-600">{child.grade}</p>
                            </div>
                          </div>
                          {child.login && (
                            <div className="ml-13 text-sm text-slate-600 space-y-1">
                              <p><span className="font-medium">Username:</span> {child.login.username}</p>
                              <p><span className="font-medium">PIN:</span> {child.login.pin}</p>
                              <p><span className="font-medium">Theme:</span> {child.login.theme}</p>
                            </div>
                          )}
                        </div>
                        
                        {/* Tokens and Actions - Right Side / Bottom on Mobile */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                          {/* Tokens Display */}
                          {child.login && (
                            <div className="text-left sm:text-right">
                              <p className="text-sm text-slate-600">Tokens</p>
                              <p className="text-xl font-bold text-amber-600">{child.login.tokens}</p>
                            </div>
                          )}
                          
                          {/* Active Toggle - Full width on mobile */}
                          {child.login && (
                            <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-start">
                              <Label className="text-xs text-slate-600">Active</Label>
                              <Switch 
                                checked={child.login.active} 
                                onCheckedChange={async (checked) => {
                                  try {
                                    await api.updateChildActiveStatus(child.id, checked);
                                    await loadChildren();
                                    toast({
                                      title: 'Status Updated',
                                      description: `Child account ${checked ? 'activated' : 'deactivated'}`
                                    });
                                  } catch (error) {
                                    toast({
                                      title: 'Error',
                                      description: error.message || 'Failed to update status',
                                      variant: 'destructive'
                                    });
                                  }
                                }}
                              />
                            </div>
                          )}
                          
                          {/* Action Buttons - Full width stacked on mobile, inline on desktop */}
                          <div className="flex flex-row gap-2 w-full sm:w-auto">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingChild(child);
                                setIsAddChildDialogOpen(true);
                              }}
                              className="flex-1 sm:flex-none text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Edit2 className="w-3 h-3 sm:mr-1" />
                              <span className="hidden sm:inline ml-1">Edit</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditPin(child)}
                              className="flex-1 sm:flex-none text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                            >
                              <Edit2 className="w-3 h-3 sm:mr-1" />
                              <span className="hidden sm:inline ml-1">PIN</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRemoveChild(child.id)}
                              className="flex-1 sm:flex-none text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {!loading && children.length === 0 && (
                  <div className="text-center py-12">
                    <User className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 text-lg mb-4">No children added yet</p>
                    <Button
                      onClick={() => setIsAddChildDialogOpen(true)}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Your First Child
                    </Button>
                  </div>
                )}

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-900">
                    💡 Child login PINs are 4-digit codes for easy access. Children use these to submit work and view their progress.
                  </p>
                </div>
              </div>
            </Card>
          </TabsContent>


          {/* Requirements Tab */}
          <TabsContent value="requirements">
            <Card className="p-8 border-0 shadow-lg bg-white">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">State Requirements</h2>
              
              {/* State Selector */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <Label>Select State</Label>
                    <p className="text-sm text-slate-600 mt-1">View requirements for any state</p>
                  </div>
                  <Select value={selectedState} onValueChange={setSelectedState}>
                    <SelectTrigger className="w-64">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {states.map(state => (
                        <SelectItem key={state} value={state}>
                          {stateRequirements[state].name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Completion Progress</span>
                    <span className="font-semibold text-green-700">
                      {completedCount} of {requirements.length} completed
                    </span>
                  </div>
                  <Progress value={requirementsProgress} className="h-3" />
                </div>
              </div>

              {/* Requirements List */}
              <div className="space-y-6">
                {Object.keys(groupedRequirements).map(category => {
                  const Icon = categoryIcons[category] || CheckCircle;
                  const colorClass = categoryColors[category] || 'bg-slate-100 text-slate-700';
                  
                  return (
                    <div key={category} className="space-y-3">
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`p-2 rounded-lg ${colorClass}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <h3 className="font-semibold text-slate-900 capitalize">
                          {category.replace('_', ' ')}
                        </h3>
                      </div>
                      
                      {groupedRequirements[category].map(req => (
                        <div
                          key={req.id}
                          className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                        >
                          <Checkbox
                            checked={req.completed}
                            onCheckedChange={() => handleToggleRequirement(req.id)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <p className={`font-medium ${req.completed ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                              {req.requirement}
                            </p>
                            {req.details && (
                              <p className="text-sm text-slate-600 mt-1">{req.details}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
                
                {requirements.length === 0 && (
                  <p className="text-center text-slate-500 py-8">
                    No requirements available for this state.
                  </p>
                )}
              </div>
            </Card>
          </TabsContent>

        </Tabs>
      </div>

      {/* Add Subject Dialog */}
      <Dialog open={isAddSubjectDialogOpen} onOpenChange={setIsAddSubjectDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-slate-900">Add New Subject</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Subject Name</Label>
              <Input
                value={subjectFormData.name}
                onChange={(e) => setSubjectFormData({ ...subjectFormData, name: e.target.value })}
                placeholder="e.g., Math, Science"
                className="mt-2"
              />
            </div>
            <div>
              <Label>Color</Label>
              <Input
                type="color"
                value={subjectFormData.color}
                onChange={(e) => setSubjectFormData({ ...subjectFormData, color: e.target.value })}
                className="mt-2 h-12"
              />
            </div>
            <div>
              <Label>Icon (Emoji)</Label>
              <Input
                value={subjectFormData.icon}
                onChange={(e) => setSubjectFormData({ ...subjectFormData, icon: e.target.value })}
                placeholder="📚"
                maxLength={2}
                className="mt-2 text-2xl"
              />
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleAddSubject}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-6"
                disabled={!subjectFormData.name}
              >
                Add Subject
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setIsAddSubjectDialogOpen(false);
                  setSubjectFormData({ name: '', color: '#3B82F6', icon: '📚' });
                }}
                className="px-6"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Subject Dialog */}
      <Dialog open={isEditSubjectDialogOpen} onOpenChange={setIsEditSubjectDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-slate-900">Edit Subject</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Subject Name</Label>
              <Input
                value={subjectFormData.name}
                onChange={(e) => setSubjectFormData({ ...subjectFormData, name: e.target.value })}
                className="mt-2"
              />
            </div>
            <div>
              <Label>Color</Label>
              <Input
                type="color"
                value={subjectFormData.color}
                onChange={(e) => setSubjectFormData({ ...subjectFormData, color: e.target.value })}
                className="mt-2 h-12"
              />
            </div>
            <div>
              <Label>Icon (Emoji)</Label>
              <Input
                value={subjectFormData.icon}
                onChange={(e) => setSubjectFormData({ ...subjectFormData, icon: e.target.value })}
                maxLength={2}
                className="mt-2 text-2xl"
              />
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleUpdateSubject}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-6"
                disabled={!subjectFormData.name}
              >
                Save Changes
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditSubjectDialogOpen(false);
                  setSelectedSubject(null);
                  setSubjectFormData({ name: '', color: '#3B82F6', icon: '📚' });
                }}
                className="px-6"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Child Dialog */}
      <Dialog open={isAddChildDialogOpen} onOpenChange={(open) => {
        setIsAddChildDialogOpen(open);
        if (!open) {
          setEditingChild(null);
          setChildFormData({
            name: '',
            grade: '',
            age: '',
            username: '',
            pin: '',
            theme: 'purple'
          });
        } else if (editingChild) {
          // Pre-fill form when editing
          setChildFormData({
            name: editingChild.name || '',
            grade: editingChild.grade || '',
            age: editingChild.age || '',
            username: editingChild.username || '',
            pin: '',
            theme: editingChild.theme || 'purple'
          });
        }
      }}>
        <DialogContent className="bg-white max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-slate-900">
              {editingChild ? 'Edit Student' : 'Add New Student'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Child's Name *</Label>
                <Input
                  value={childFormData.name}
                  onChange={(e) => setChildFormData({ ...childFormData, name: e.target.value })}
                  placeholder="Emma Johnson"
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Grade *</Label>
                <Select
                  value={childFormData.grade}
                  onValueChange={(value) => setChildFormData({ ...childFormData, grade: value })}
                >
                  <SelectTrigger className="mt-2" data-testid="child-grade-select">
                    <SelectValue placeholder="Select grade" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {['Pre-K', 'Kindergarten', '1st Grade', '2nd Grade', '3rd Grade', '4th Grade', '5th Grade', '6th Grade', '7th Grade', '8th Grade', '9th Grade', '10th Grade', '11th Grade', '12th Grade'].map(g => (
                      <SelectItem key={g} value={g}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Age (Optional)</Label>
              <Input
                type="number"
                value={childFormData.age}
                onChange={(e) => setChildFormData({ ...childFormData, age: e.target.value })}
                placeholder="10"
                className="mt-2"
              />
            </div>

            {!editingChild && (
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Username *</Label>
                  <Input
                    value={childFormData.username}
                    onChange={(e) => setChildFormData({ ...childFormData, username: e.target.value.toLowerCase() })}
                    placeholder="emma"
                    className="mt-2"
                  />
                  <p className="text-xs text-slate-500 mt-1">Used for logging in</p>
                </div>
                <div>
                  <Label>PIN (4 digits) *</Label>
                  <Input
                  type="text"
                  maxLength={4}
                  value={childFormData.pin}
                  onChange={(e) => setChildFormData({ ...childFormData, pin: e.target.value.replace(/\D/g, '') })}
                  placeholder="1234"
                  className="mt-2"
                />
                <p className="text-xs text-slate-500 mt-1">Must be 4 digits</p>
                </div>
              </div>
            )}

            <div>
              <Label>Theme</Label>
              <Select value={childFormData.theme} onValueChange={(value) => setChildFormData({ ...childFormData, theme: value })}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="purple">Purple/Pink</SelectItem>
                  <SelectItem value="blue">Blue/Teal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleAddChild}
                className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white py-6"
              >
                Add Child
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsAddChildDialogOpen(false)}
                className="px-6"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit PIN Dialog */}
      <Dialog open={isEditPinDialogOpen} onOpenChange={setIsEditPinDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-slate-900">Change PIN</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedChildLogin && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600 mb-1">Child</p>
                <p className="font-semibold text-slate-900">
                  {children.find(c => c.id === selectedChildLogin.child_id)?.name || selectedChildLogin.username}
                </p>
              </div>
            )}

            <div>
              <Label>New PIN (4 digits)</Label>
              <Input
                type="text"
                maxLength={4}
                value={childFormData.pin}
                onChange={(e) => setChildFormData({ ...childFormData, pin: e.target.value.replace(/\D/g, '') })}
                placeholder="1234"
                className="mt-2 text-center text-2xl tracking-widest"
              />
              <p className="text-xs text-slate-500 mt-1">Must be exactly 4 digits</p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleSavePin}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-6"
              >
                Save PIN
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsEditPinDialogOpen(false)}
                className="px-6"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-slate-900">Change Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="currentPassword">Current Password</Label>
              <div className="relative mt-2">
                <Input
                  id="currentPassword"
                  type={showCurrentPassword ? "text" : "password"}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  placeholder="Enter current password"
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                >
                  {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative mt-2">
                <Input
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  placeholder="Enter new password"
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Must be at least 8 characters</p>
            </div>
            <div>
              <Label htmlFor="confirmNewPassword">Confirm New Password</Label>
              <div className="relative mt-2">
                <Input
                  id="confirmNewPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  placeholder="Confirm new password"
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                onClick={() => setShowChangePassword(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={passwordLoading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                {passwordLoading ? 'Changing...' : 'Change Password'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Dialog */}
      <Dialog open={showCancelSubscription} onOpenChange={setShowCancelSubscription}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-slate-900">Cancel Subscription</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-900 mb-1">Are you sure?</h4>
                  <p className="text-sm text-amber-800">
                    Cancelling your subscription will:
                  </p>
                  <ul className="text-sm text-amber-800 list-disc list-inside mt-2 space-y-1">
                    <li>End your access at the end of the current billing period</li>
                    <li>Remove access to all premium features</li>
                    <li>Delete all your data after 30 days</li>
                  </ul>
                </div>
              </div>
            </div>
            <p className="text-slate-600">
              We're sorry to see you go! If you're having issues, please contact our support team at support@edutracker.app
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowCancelSubscription(false)}
                variant="outline"
                className="flex-1"
              >
                Keep Subscription
              </Button>
              <Button
                onClick={handleCancelSubscription}
                disabled={cancelLoading}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {cancelLoading ? 'Cancelling...' : 'Cancel Subscription'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Tokens Dialog */}
      <Dialog open={isEditTokensDialogOpen} onOpenChange={setIsEditTokensDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Token Balance</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {editingChildTokens && (
              <>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: editingChildTokens.profile_color || '#3B82F6' }}
                  >
                    {editingChildTokens.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{editingChildTokens.name}</p>
                    <p className="text-sm text-slate-600">Current: {editingChildTokens.login?.tokens || 0} tokens</p>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="tokenAmount">New Token Amount</Label>
                  <Input
                    id="tokenAmount"
                    type="number"
                    min="0"
                    value={newTokenAmount}
                    onChange={(e) => setNewTokenAmount(parseInt(e.target.value) || 0)}
                    className="mt-2"
                    placeholder="Enter token amount"
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    Use this to give bonus tokens or make corrections
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => setIsEditTokensDialogOpen(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleUpdateTokens}
                    className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    <Award className="w-4 h-4 mr-2" />
                    Update Tokens
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default SettingsPage;